import { createTheme, Theme } from '@mui/material/styles';

// Extend MUI palette with sidebar colors
declare module '@mui/material/styles' {
  interface Palette {
    sidebar: { main: string; active: string };
  }
  interface PaletteOptions {
    sidebar?: { main: string; active: string };
  }
}

const sharedTypography = {
  fontFamily: '"DM Sans", sans-serif',
  h1: { fontFamily: '"Fraunces", serif', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' },
  h2: { fontFamily: '"Fraunces", serif', fontSize: 'clamp(1.5rem, 3.5vw, 2rem)' },
  h3: { fontFamily: '"Fraunces", serif', fontSize: 'clamp(1.25rem, 3vw, 1.75rem)' },
  h4: { fontFamily: '"Fraunces", serif', fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)' },
  h5: { fontFamily: '"Fraunces", serif', fontSize: 'clamp(1rem, 2vw, 1.25rem)' },
  h6: { fontFamily: '"Fraunces", serif', fontSize: 'clamp(0.875rem, 1.5vw, 1.125rem)' },
};

const sharedShape = { borderRadius: 12 };

function sharedComponents(primary: string, primaryDark: string, secondary: string, secondaryDark: string, divider: string) {
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          minHeight: 44,
          borderRadius: 8,
        },
        containedPrimary: {
          backgroundColor: primary,
          color: '#FFFFFF',
          '&:hover': { backgroundColor: primaryDark },
        },
        containedSecondary: {
          backgroundColor: secondary,
          color: '#FFFFFF',
          '&:hover': { backgroundColor: secondaryDark },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${divider}`,
          boxShadow: '0 1px 3px rgba(74,55,40,0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { minWidth: 44, minHeight: 44 },
      },
    },
    MuiDialog: {
      defaultProps: {
        'aria-modal': true as const,
      },
    },
  };
}

/** Paper mode theme — warm teal (current default) */
export const paperTheme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#3A8C8C', light: '#6BB8B8', dark: '#2A6666' },
    secondary: { main: '#E8A045', light: '#F5C97A', dark: '#C07830' },
    error: { main: '#C0392B' },
    success: { main: '#27AE60' },
    background: { default: '#FDF6EC', paper: '#FFFFFF' },
    text: { primary: '#4A3728', secondary: '#7A5F4E' },
    divider: '#F0E4CE',
    sidebar: { main: '#2A6666', active: '#3A8C8C' },
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: sharedComponents('#3A8C8C', '#2A6666', '#E8A045', '#C07830', '#F0E4CE'),
});

/** Live mode theme — royal blue / navy */
export const liveTheme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1D4ED8', light: '#3B82F6', dark: '#1E3A8A' },
    secondary: { main: '#F59E0B', light: '#FCD34D', dark: '#D97706' },
    error: { main: '#C0392B' },
    success: { main: '#27AE60' },
    background: { default: '#F0F4FF', paper: '#FFFFFF' },
    text: { primary: '#1E293B', secondary: '#475569' },
    divider: '#DBEAFE',
    sidebar: { main: '#0F172A', active: '#1D4ED8' },
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: sharedComponents('#1D4ED8', '#1E3A8A', '#F59E0B', '#D97706', '#DBEAFE'),
});
